import { Box, List, ListItem, makeStyles, Popover, Typography, withStyles } from "@material-ui/core";
import Brightness1Icon from "@material-ui/icons/Brightness1";
import ClearIcon from "@material-ui/icons/Clear";
import { useEffect, useMemo, useRef, useState } from "react";
import { THEME_MODE } from "src/config/constants";
import { COLOR_ARRAY } from "src/config/styleConfig";
import useNotify from "src/hooks/useNotify";
import ArrowAnimationIcon from "../Icon/ArrowAnimationIcon";

const CssBox = withStyles((theme) => ({
  root: {
    background: theme.palette.type === THEME_MODE.DARK ? "#233345" : "#F4F6FA",
    border: `1px solid ${theme.palette.type === THEME_MODE.DARK ? "#334965" : "#E6E6E6"}`,
    borderRadius: "10px",
    padding: "0.25rem 0.25rem",
    cursor: "pointer",
    minHeight: "44px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
}))(Box);

const ItemBox = withStyles((theme) => ({
  root: {
    background: theme.palette.type === THEME_MODE.DARK ? "#031527" : "#FFFFFF",
    borderRadius: "6px",
    padding: "0.25rem 0.25rem 0.25rem 0.25rem",
    margin: "0.175rem 0rem 0.175rem 0.25rem",
  },
}))(Box);

const useStyle = makeStyles((theme) => ({
  icon: {
    fontSize: "14px",
    color: theme.palette.text.disabled,
  },
}));

export default function MultipleSelector({ items, defaultSelectedItems, event, Component, config, ...props }) {
  const cls = useStyle();
  const rootRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const id = open ? "multiple-selector-popover" : undefined;

  const { warnNotify } = useNotify();
  const [selectedItems, setSelectedItems] = useState(defaultSelectedItems);

  useEffect(() => {
    if (selectedItems.length == 0) setSelectedItems(defaultSelectedItems);
  }, [defaultSelectedItems]);

  const rootWidth = useMemo(() => {
    if (rootRef?.current) return rootRef.current.offsetWidth;
    else return "auto";
  }, [rootRef?.current]);

  const selectedLabel = selectedItems.map((item) => item.label);

  const realItems = useMemo(() => {
    return items.filter((item) => !selectedLabel.includes(item.label));
  }, [items, selectedLabel]);

  function onChooseItem(e, item) {
    if (config?.maxItem) {
      if (selectedItems.length >= config.maxItem) {
        setAnchorEl(null);
        return warnNotify(`Selected items is less than ${config.maxItem} items`);
      }
    }
    const _temp = [...selectedItems];
    _temp.push(item);
    setSelectedItems(_temp);
    if (event?.onChooseItem) event.onChooseItem(e, item, _temp);
    setAnchorEl(null);
  }

  function onRemoveItem(e, item) {
    e.stopPropagation();
    let _temp = [...selectedItems];
    const index = _temp.indexOf(item);
    if (index > -1) _temp.splice(index, 1);
    setSelectedItems(_temp);
    if (event?.onRemoveItem) event.onRemoveItem(e, item, _temp);
  }

  function Item({ item, index }) {
    return (
      <ItemBox display="inline-flex" justifyContent="flex-start" alignItems="center">
        <Brightness1Icon className={cls.icon} style={{ marginRight: "0.25rem", color: COLOR_ARRAY[index] }} />
        <Typography className={cls.icon}>{item.label}</Typography>
        <ClearIcon className={cls.icon} onClick={(event) => onRemoveItem(event, item)} />
      </ItemBox>
    );
  }

  return (
    <>
      <CssBox {...props} ref={rootRef} onClick={(event) => setAnchorEl(event.currentTarget)}>
        <Box display="flex" flexWrap="wrap">
          {selectedItems.map((item, index) => {
            return <Item key={item.id} item={item} index={index} />;
          })}
        </Box>
        <Box display="flex" alignItems="center" width="25px" px="2px">
          <ArrowAnimationIcon fontSize="small" isTransform={open} />
        </Box>
      </CssBox>
      <Popover id={id} open={open} anchorEl={anchorEl} onClose={() => setAnchorEl(null)}>
        {Component?.start}
        <List style={{ width: rootWidth }}>
          {realItems.map((item, _) => {
            return (
              <ListItem
                button
                key={item.id}
                onClick={(event) => onChooseItem(event, item)}
                style={{ borderRadius: "6px" }}
              >
                {item.label}
              </ListItem>
            );
          })}
        </List>
        {Component?.end}
      </Popover>
    </>
  );
}
